import { Role } from './rbac';

// User permissions structure matching Healthie API
export interface UserPermissions {
  // Client Management
  can_add_clients?: boolean;
  can_archive_clients?: boolean;
  can_merge_clients?: boolean;
  
  // Scheduling
  can_schedule_appointments?: boolean;
  can_edit_appointment_types?: boolean;
  
  // Notes & Documentation
  can_delete_charting_notes?: boolean;
  can_lock_own_charting_notes?: boolean;
  can_lock_others_charting_notes?: boolean;
  can_sign_own_charting_notes?: boolean;
  can_sign_others_charting_notes?: boolean;
  can_unlock_charting_notes?: boolean;
  can_write_org_addendums?: boolean;
  cannot_lock_form_answer_groups?: boolean;
  
  // Billing & Payments
  can_charge_clients?: boolean;
  can_check_eligibility?: boolean;
  can_edit_credit?: boolean;
  can_see_transfers?: boolean;
  
  // Forms & Documents
  can_edit_forms?: boolean;
  can_edit_docs?: boolean;
  
  // Labs & Clinical
  can_order_labs?: boolean;
  can_view_labs?: boolean;
  can_see_insurance_authorization?: boolean;
  can_see_option_to_use_erx_dosespot?: boolean;
  
  // Organization & Settings
  can_edit_settings?: boolean;
  can_edit_brand?: boolean;
  /** Edit own practitioner profile fields (name, bio, phone, credentials). Available to all clinical staff. */
  can_edit_own_profile?: boolean;
  /** Manage org members (create, update, deactivate). Admin roles only. Matches PRD `edit_users` capability. */
  edit_users?: boolean;
  can_edit_org_calendar?: boolean;
  can_edit_packages?: boolean;
  can_edit_products?: boolean;
  can_edit_programs?: boolean;
  can_edit_program_clients?: boolean;
  cannot_access_org_member_settings?: boolean;
  
  // Audit Logs
  can_see_audit_log_ui?: boolean;
  can_delete_faxes?: boolean;
  
  // Tasks & Collaboration
  can_mark_assigned_tasks_to_other_org_members_as_completed?: boolean;
  
  // Advanced Features
  can_see_fullscript_tab?: boolean;
  has_developer_access?: boolean;

  // Therapy Workflow Permissions
  can_manage_group_sessions?: boolean;
  can_manage_iop_programs?: boolean;
  can_manage_family_therapy?: boolean;
  can_view_attendance?: boolean;
  can_edit_attendance?: boolean;
  can_view_billing_guidance?: boolean;
  can_submit_claims?: boolean;
  can_view_claims?: boolean;
  can_manage_care_teams?: boolean;
  can_cosign_notes?: boolean;
  
  // UI Visibility Controls
  show_appointments_tab?: boolean;
  show_billing_tab?: boolean;
  show_billing_dropdown_item?: boolean;
  /** Staff client profile: Billing tab (insurance, authorizations, billing history). */
  show_client_profile_billing_tab?: boolean;
  /** Staff client profile: Contacts tab (emergency contacts, ROI). */
  show_client_profile_contacts_tab?: boolean;
  show_brand_tab?: boolean;
  show_care_plan_area?: boolean;
  show_chat_tab?: boolean;
  show_cms1500s_tab?: boolean;
  show_dashboard?: boolean;
  show_documents_tab?: boolean;
  show_education_tab?: boolean;
  show_forms_tab?: boolean;
  show_goals_area?: boolean;
  show_integrations_dropdown_item?: boolean;
  show_journal_entries_area?: boolean;
  show_org_tab?: boolean;
  show_packages_tab?: boolean;
  show_payment_methods_dropdown_item?: boolean;
  show_payments_tab?: boolean;
  show_received_fax_tab?: boolean;
  show_reports_tab?: boolean;
  show_sent_fax_tab?: boolean;
  show_superbills_tab?: boolean;
}

export interface Permission {
  id: keyof UserPermissions;
  label: string;
  category: string;
  description?: string;
}

export interface PermissionCategory {
  id: string;
  label: string;
  permissions: Permission[];
}

export const permissionCategories: PermissionCategory[] = [
  {
    id: 'clients',
    label: 'Client Management',
    permissions: [
      { id: 'can_add_clients', label: 'Add clients', category: 'clients', description: 'Allow this user to create new client accounts' },
      { id: 'can_archive_clients', label: 'Archive clients', category: 'clients', description: 'Allow this user to archive/deactivate clients' },
      { id: 'can_merge_clients', label: 'Merge clients', category: 'clients', description: 'Allow this user to merge duplicate client records' },
    ]
  },
  {
    id: 'scheduling',
    label: 'Scheduling & Appointments',
    permissions: [
      { id: 'can_schedule_appointments', label: 'Schedule appointments', category: 'scheduling', description: 'Allow this user to create and manage appointments' },
      { id: 'can_edit_appointment_types', label: 'Edit appointment types', category: 'scheduling', description: 'Allow this user to modify appointment type settings' },
      { id: 'show_appointments_tab', label: 'Show appointments tab', category: 'scheduling', description: 'Display the appointments tab in the sidebar' },
    ]
  },
  {
    id: 'notes',
    label: 'Notes & Documentation',
    permissions: [
      { id: 'can_delete_charting_notes', label: 'Delete charting notes', category: 'notes', description: 'Allow this user to permanently delete notes' },
      { id: 'can_lock_own_charting_notes', label: 'Lock own charting notes', category: 'notes', description: 'Allow this user to lock their own notes' },
      { id: 'can_lock_others_charting_notes', label: 'Lock others\' charting notes', category: 'notes', description: 'Allow this user to lock notes created by others' },
      { id: 'can_sign_own_charting_notes', label: 'Sign own charting notes', category: 'notes', description: 'Allow this user to sign/finalize their own notes' },
      { id: 'can_sign_others_charting_notes', label: 'Sign others\' charting notes', category: 'notes', description: 'Allow this user to sign notes created by others' },
      { id: 'can_unlock_charting_notes', label: 'Unlock charting notes', category: 'notes', description: 'Allow this user to unlock previously locked notes' },
      { id: 'can_write_org_addendums', label: 'Write org addendums', category: 'notes', description: 'Allow this user to add addendums to locked forms' },
      { id: 'cannot_lock_form_answer_groups', label: 'Cannot lock form answer groups', category: 'notes', description: 'Prevent this user from locking form responses' },
    ]
  },
  {
    id: 'billing',
    label: 'Billing & Payments',
    permissions: [
      { id: 'can_charge_clients', label: 'Charge clients', category: 'billing', description: 'Allow this user to process client payments' },
      { id: 'can_check_eligibility', label: 'Check insurance eligibility', category: 'billing', description: 'Allow this user to verify insurance eligibility' },
      { id: 'can_edit_credit', label: 'Edit client credit', category: 'billing', description: 'Allow this user to manage client credit balances' },
      { id: 'can_see_transfers', label: 'View transfers', category: 'billing', description: 'Allow this user to see transfer/payout information' },
      { id: 'show_billing_tab', label: 'Show billing tab', category: 'billing', description: 'Display the billing tab in the sidebar' },
      { id: 'show_billing_dropdown_item', label: 'Show billing dropdown item', category: 'billing', description: 'Show billing option in patient header menu' },
      { id: 'show_client_profile_billing_tab', label: 'Show client profile billing tab', category: 'billing', description: 'Display billing on the staff client profile page' },
      { id: 'show_client_profile_contacts_tab', label: 'Show client profile contacts tab', category: 'billing', description: 'Display contacts on the staff client profile page' },
      { id: 'show_payments_tab', label: 'Show payments tab', category: 'billing', description: 'Display the payments tab in the sidebar' },
      { id: 'show_payment_methods_dropdown_item', label: 'Show payment methods option', category: 'billing', description: 'Show payment methods in settings dropdown' },
      { id: 'show_cms1500s_tab', label: 'Show CMS-1500s tab', category: 'billing', description: 'Display the CMS-1500 forms tab' },
      { id: 'show_superbills_tab', label: 'Show superbills tab', category: 'billing', description: 'Display the superbills tab in the sidebar' },
    ]
  },
  {
    id: 'forms-documents',
    label: 'Forms & Documents',
    permissions: [
      { id: 'can_edit_forms', label: 'Edit forms', category: 'forms-documents', description: 'Allow this user to create and modify forms' },
      { id: 'can_edit_docs', label: 'Edit documents', category: 'forms-documents', description: 'Allow this user to manage organization documents' },
      { id: 'show_forms_tab', label: 'Show forms tab', category: 'forms-documents', description: 'Display the forms tab in the sidebar' },
      { id: 'show_documents_tab', label: 'Show documents tab', category: 'forms-documents', description: 'Display the documents tab in the sidebar' },
    ]
  },
  {
    id: 'clinical',
    label: 'Clinical & Labs',
    permissions: [
      { id: 'can_order_labs', label: 'Order labs', category: 'clinical', description: 'Allow this user to order laboratory tests' },
      { id: 'can_view_labs', label: 'View labs', category: 'clinical', description: 'Allow this user to access lab results' },
      { id: 'can_see_insurance_authorization', label: 'View insurance authorization', category: 'clinical', description: 'Allow this user to see insurance authorization info' },
      { id: 'can_see_option_to_use_erx_dosespot', label: 'Access e-prescribing (Dosespot)', category: 'clinical', description: 'Show option to use electronic prescribing' },
      { id: 'show_care_plan_area', label: 'Show care plan area', category: 'clinical', description: 'Display care plan sections' },
      { id: 'show_goals_area', label: 'Show goals area', category: 'clinical', description: 'Display goals and tracking sections' },
    ]
  },
  {
    id: 'organization',
    label: 'Organization & Settings',
    permissions: [
      { id: 'can_edit_settings', label: 'Edit account settings', category: 'organization', description: 'Allow this user to modify account settings' },
      { id: 'can_edit_brand', label: 'Edit brand', category: 'organization', description: 'Allow this user to customize branding' },
      { id: 'can_edit_own_profile', label: 'Edit own profile', category: 'organization', description: 'Allow this user to edit their own name, bio, and contact info' },
      { id: 'edit_users', label: 'Manage users', category: 'organization', description: 'Allow this user to create, update, and deactivate org members' },
      { id: 'can_edit_org_calendar', label: 'Edit org calendar', category: 'organization', description: 'Allow this user to manage organization calendar' },
      { id: 'can_edit_packages', label: 'Edit packages', category: 'organization', description: 'Allow this user to create and modify service packages' },
      { id: 'can_edit_products', label: 'Edit products', category: 'organization', description: 'Allow this user to manage products' },
      { id: 'can_edit_programs', label: 'Edit programs', category: 'organization', description: 'Allow this user to create and modify programs' },
      { id: 'can_edit_program_clients', label: 'Edit program clients', category: 'organization', description: 'Allow this user to add/remove clients from programs' },
      { id: 'cannot_access_org_member_settings', label: 'Cannot access org member settings', category: 'organization', description: 'Prevent access to org member settings' },
      { id: 'show_org_tab', label: 'Show organization tab', category: 'organization', description: 'Display the organization tab in the sidebar' },
      { id: 'show_brand_tab', label: 'Show brand tab', category: 'organization', description: 'Display brand customization in header' },
      { id: 'show_integrations_dropdown_item', label: 'Show integrations option', category: 'organization', description: 'Show integrations in settings dropdown' },
      { id: 'show_packages_tab', label: 'Show packages tab', category: 'organization', description: 'Display the packages tab in the sidebar' },
    ]
  },
  {
    id: 'compliance',
    label: 'Audit Logs',
    permissions: [
      { id: 'can_see_audit_log_ui', label: 'View audit logs', category: 'compliance', description: 'Allow this user to access audit log interface' },
      { id: 'can_delete_faxes', label: 'Delete faxes', category: 'compliance', description: 'Allow this user to delete received faxes' },
      { id: 'show_received_fax_tab', label: 'Show received fax tab', category: 'compliance', description: 'Display received faxes tab' },
      { id: 'show_sent_fax_tab', label: 'Show sent fax tab', category: 'compliance', description: 'Display sent faxes tab' },
    ]
  },
  {
    id: 'collaboration',
    label: 'Tasks & Collaboration',
    permissions: [
      { id: 'can_mark_assigned_tasks_to_other_org_members_as_completed', label: 'Complete others\' tasks', category: 'collaboration', description: 'Mark tasks assigned to other members as complete' },
      { id: 'show_chat_tab', label: 'Show chat tab', category: 'collaboration', description: 'Display the messaging/chat tab' },
    ]
  },
  {
    id: 'therapy-workflows',
    label: 'Therapy Workflows',
    permissions: [
      { id: 'can_manage_group_sessions', label: 'Manage group sessions', category: 'therapy-workflows', description: 'Create and manage group therapy sessions and rosters' },
      { id: 'can_manage_iop_programs', label: 'Manage IOP programs', category: 'therapy-workflows', description: 'Create and manage Intensive Outpatient Program encounters' },
      { id: 'can_manage_family_therapy', label: 'Manage family therapy', category: 'therapy-workflows', description: 'Set up family therapy with related persons and IP tracking' },
      { id: 'can_view_attendance', label: 'View attendance', category: 'therapy-workflows', description: 'View attendance records for sessions' },
      { id: 'can_edit_attendance', label: 'Edit attendance', category: 'therapy-workflows', description: 'Mark and update attendance for session participants' },
      { id: 'can_view_billing_guidance', label: 'View billing guidance', category: 'therapy-workflows', description: 'See CPT code recommendations and billing readiness' },
      { id: 'can_submit_claims', label: 'Submit claims', category: 'therapy-workflows', description: 'Submit claims for billing' },
      { id: 'can_view_claims', label: 'View claims', category: 'therapy-workflows', description: 'View claim status and responses' },
      { id: 'can_manage_care_teams', label: 'Manage care teams', category: 'therapy-workflows', description: 'Add or remove members from care teams' },
      { id: 'can_cosign_notes', label: 'Co-sign notes', category: 'therapy-workflows', description: 'Co-sign clinical notes (supervisors)' },
    ],
  },
  {
    id: 'advanced',
    label: 'Advanced Features',
    permissions: [
      { id: 'can_see_fullscript_tab', label: 'View Fullscript tab', category: 'advanced', description: 'Access Fullscript integration features' },
      { id: 'has_developer_access', label: 'Developer access', category: 'advanced', description: 'Allow access to API keys and webhooks' },
      { id: 'show_dashboard', label: 'Show dashboard', category: 'advanced', description: 'Display the dashboard view' },
      { id: 'show_education_tab', label: 'Show education tab', category: 'advanced', description: 'Display the education/courses tab' },
      { id: 'show_journal_entries_area', label: 'Show journal entries', category: 'advanced', description: 'Display journal/tracking sections' },
      { id: 'show_reports_tab', label: 'Show reports tab', category: 'advanced', description: 'Display the reports tab in the sidebar' },
    ]
  }
];

// Default permissions for each role (as boolean objects)
export const defaultRolePermissions: Record<Role, Partial<UserPermissions>> = {
  'unify-admin': {
    can_add_clients: true,
    can_archive_clients: true,
    can_merge_clients: true,
    can_schedule_appointments: true,
    can_edit_appointment_types: true,
    can_delete_charting_notes: true,
    can_lock_own_charting_notes: true,
    can_lock_others_charting_notes: true,
    can_sign_own_charting_notes: true,
    can_sign_others_charting_notes: true,
    can_unlock_charting_notes: true,
    can_write_org_addendums: true,
    cannot_lock_form_answer_groups: false,
    can_charge_clients: true,
    can_check_eligibility: true,
    can_edit_credit: true,
    can_see_transfers: true,
    can_edit_forms: true,
    can_edit_docs: true,
    can_order_labs: true,
    can_view_labs: true,
    can_see_insurance_authorization: true,
    can_see_option_to_use_erx_dosespot: true,
    can_edit_settings: true,
    can_edit_brand: true,
    can_edit_own_profile: true,
    edit_users: true,
    can_edit_org_calendar: true,
    can_edit_packages: true,
    can_edit_products: true,
    can_edit_programs: true,
    can_edit_program_clients: true,
    cannot_access_org_member_settings: false,
    can_see_audit_log_ui: true,
    can_delete_faxes: true,
    can_mark_assigned_tasks_to_other_org_members_as_completed: true,
    can_see_fullscript_tab: true,
    has_developer_access: true,
    can_manage_group_sessions: true,
    can_manage_iop_programs: true,
    can_manage_family_therapy: true,
    can_view_attendance: true,
    can_edit_attendance: true,
    can_view_billing_guidance: true,
    can_submit_claims: true,
    can_view_claims: true,
    can_manage_care_teams: true,
    can_cosign_notes: true,
    show_appointments_tab: true,
    show_billing_tab: true,
    show_billing_dropdown_item: true,
    show_client_profile_billing_tab: true,
    show_client_profile_contacts_tab: true,
    show_brand_tab: true,
    show_care_plan_area: true,
    show_chat_tab: true,
    show_cms1500s_tab: true,
    show_dashboard: true,
    show_documents_tab: true,
    show_education_tab: true,
    show_forms_tab: true,
    show_goals_area: true,
    show_integrations_dropdown_item: true,
    show_journal_entries_area: true,
    show_org_tab: true,
    show_packages_tab: true,
    show_payment_methods_dropdown_item: true,
    show_payments_tab: true,
    show_received_fax_tab: true,
    show_reports_tab: true,
    show_sent_fax_tab: true,
    show_superbills_tab: true,
  },
  'unify-assistant': {
    // Platform-wide operational support; cannot edit Unify project or organization settings.
    can_schedule_appointments: true,
    can_check_eligibility: true,
    can_edit_own_profile: true,
    can_edit_org_calendar: true,
    can_view_labs: true,
    can_see_insurance_authorization: true,
    can_view_attendance: true,
    can_view_billing_guidance: true,
    can_view_claims: true,
    can_manage_care_teams: true,
    can_see_audit_log_ui: true,
    show_appointments_tab: true,
    show_billing_tab: true,
    show_billing_dropdown_item: true,
    show_client_profile_billing_tab: true,
    show_client_profile_contacts_tab: true,
    show_chat_tab: true,
    show_documents_tab: true,
    show_forms_tab: true,
    show_org_tab: true,
    show_reports_tab: true,
  },
  'practice-owner': {
    can_add_clients: true,
    can_archive_clients: true,
    can_merge_clients: true,
    can_schedule_appointments: true,
    can_edit_appointment_types: true,
    can_delete_charting_notes: true,
    can_lock_own_charting_notes: true,
    can_lock_others_charting_notes: true,
    can_sign_own_charting_notes: true,
    can_sign_others_charting_notes: true,
    can_unlock_charting_notes: true,
    can_write_org_addendums: true,
    can_charge_clients: true,
    can_check_eligibility: true,
    can_edit_credit: true,
    can_see_transfers: true,
    can_edit_forms: true,
    can_edit_docs: true,
    can_order_labs: true,
    can_view_labs: true,
    can_see_insurance_authorization: true,
    can_see_option_to_use_erx_dosespot: true,
    can_edit_settings: true,
    can_edit_brand: true,
    can_edit_own_profile: true,
    edit_users: true,
    can_edit_org_calendar: true,
    can_edit_packages: true,
    can_edit_products: true,
    can_edit_programs: true,
    can_edit_program_clients: true,
    can_delete_faxes: true,
    can_mark_assigned_tasks_to_other_org_members_as_completed: true,
    can_see_fullscript_tab: true,
    can_manage_group_sessions: true,
    can_manage_iop_programs: true,
    can_manage_family_therapy: true,
    can_view_attendance: true,
    can_edit_attendance: true,
    can_view_billing_guidance: true,
    can_submit_claims: true,
    can_view_claims: true,
    can_manage_care_teams: true,
    can_cosign_notes: true,
    show_appointments_tab: true,
    show_billing_tab: true,
    show_billing_dropdown_item: true,
    show_client_profile_billing_tab: true,
    show_client_profile_contacts_tab: true,
    show_brand_tab: true,
    show_care_plan_area: true,
    show_chat_tab: true,
    show_cms1500s_tab: true,
    show_dashboard: true,
    show_documents_tab: true,
    show_education_tab: true,
    show_forms_tab: true,
    show_goals_area: true,
    show_integrations_dropdown_item: true,
    show_journal_entries_area: true,
    show_org_tab: true,
    show_packages_tab: true,
    show_payment_methods_dropdown_item: true,
    show_payments_tab: true,
    show_received_fax_tab: true,
    show_reports_tab: true,
    show_sent_fax_tab: true,
    show_superbills_tab: true,
  },
  /** Practice Admin has the same capability set as Practice Owner per PRD. */
  'practice-admin': {
    can_add_clients: true,
    can_archive_clients: true,
    can_merge_clients: true,
    can_schedule_appointments: true,
    can_edit_appointment_types: true,
    can_delete_charting_notes: true,
    can_lock_own_charting_notes: true,
    can_lock_others_charting_notes: true,
    can_sign_own_charting_notes: true,
    can_sign_others_charting_notes: true,
    can_unlock_charting_notes: true,
    can_write_org_addendums: true,
    can_charge_clients: true,
    can_check_eligibility: true,
    can_edit_credit: true,
    can_see_transfers: true,
    can_edit_forms: true,
    can_edit_docs: true,
    can_order_labs: true,
    can_view_labs: true,
    can_see_insurance_authorization: true,
    can_see_option_to_use_erx_dosespot: true,
    can_edit_settings: true,
    can_edit_brand: true,
    can_edit_own_profile: true,
    edit_users: true,
    can_edit_org_calendar: true,
    can_edit_packages: true,
    can_edit_products: true,
    can_edit_programs: true,
    can_edit_program_clients: true,
    can_delete_faxes: true,
    can_mark_assigned_tasks_to_other_org_members_as_completed: true,
    can_see_fullscript_tab: true,
    can_manage_group_sessions: true,
    can_manage_iop_programs: true,
    can_manage_family_therapy: true,
    can_view_attendance: true,
    can_edit_attendance: true,
    can_view_billing_guidance: true,
    can_submit_claims: true,
    can_view_claims: true,
    can_manage_care_teams: true,
    can_cosign_notes: true,
    show_appointments_tab: true,
    show_billing_tab: true,
    show_billing_dropdown_item: true,
    show_client_profile_billing_tab: true,
    show_client_profile_contacts_tab: true,
    show_brand_tab: true,
    show_care_plan_area: true,
    show_chat_tab: true,
    show_cms1500s_tab: true,
    show_dashboard: true,
    show_documents_tab: true,
    show_education_tab: true,
    show_forms_tab: true,
    show_goals_area: true,
    show_integrations_dropdown_item: true,
    show_journal_entries_area: true,
    show_org_tab: true,
    show_packages_tab: true,
    show_payment_methods_dropdown_item: true,
    show_payments_tab: true,
    show_received_fax_tab: true,
    show_reports_tab: true,
    show_sent_fax_tab: true,
    show_superbills_tab: true,
  },
  'practice-assistant': {
    // Practice-level operational support; limited settings access, no user management.
    can_schedule_appointments: true,
    can_charge_clients: true,
    can_check_eligibility: true,
    can_edit_own_profile: true,
    can_view_labs: true,
    can_see_insurance_authorization: true,
    can_view_attendance: true,
    can_view_billing_guidance: true,
    can_view_claims: true,
    show_appointments_tab: true,
    show_billing_tab: true,
    show_client_profile_billing_tab: true,
    show_client_profile_contacts_tab: true,
    show_chat_tab: true,
    show_dashboard: true,
    show_documents_tab: true,
    show_forms_tab: true,
    show_payments_tab: true,
  },
  'supervisor': {
    can_schedule_appointments: true,
    can_edit_own_profile: true,
    can_lock_own_charting_notes: true,
    can_sign_own_charting_notes: true,
    can_sign_others_charting_notes: true,
    can_check_eligibility: true,
    can_order_labs: true,
    can_view_labs: true,
    can_see_insurance_authorization: true,
    can_manage_group_sessions: true,
    can_manage_iop_programs: true,
    can_manage_family_therapy: true,
    can_view_attendance: true,
    can_edit_attendance: true,
    can_view_billing_guidance: true,
    can_view_claims: true,
    can_cosign_notes: true,
    show_appointments_tab: true,
    show_billing_tab: true,
    show_client_profile_billing_tab: false,
    show_client_profile_contacts_tab: true,
    show_care_plan_area: true,
    show_chat_tab: true,
    show_dashboard: true,
    show_documents_tab: true,
    show_forms_tab: true,
    show_goals_area: true,
    show_journal_entries_area: true,
    show_reports_tab: true,
  },
  'provider': {
    can_schedule_appointments: true,
    can_edit_own_profile: true,
    can_lock_own_charting_notes: true,
    can_sign_own_charting_notes: true,
    can_order_labs: true,
    can_view_labs: true,
    can_see_option_to_use_erx_dosespot: true,
    can_manage_group_sessions: true,
    can_manage_family_therapy: true,
    can_view_attendance: true,
    can_edit_attendance: true,
    can_view_billing_guidance: true,
    can_view_claims: true,
    show_appointments_tab: true,
    show_client_profile_billing_tab: false,
    show_client_profile_contacts_tab: true,
    show_care_plan_area: true,
    show_chat_tab: true,
    show_dashboard: true,
    show_documents_tab: true,
    show_forms_tab: true,
    show_goals_area: true,
    show_journal_entries_area: true,
  },
  'client': {
    show_appointments_tab: true,
    show_billing_tab: true,
    show_chat_tab: true,
    show_documents_tab: true,
    show_goals_area: true,
    show_journal_entries_area: true,
  },
};
